import { useContext, Dispatch, SetStateAction } from 'react';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from '../globalization/useTranslation';
import {
  DeleteDocumentsResponse,
  DeleteDocumentsRequest,
  CpsSearchQueryResult,
  CpsSearchQueryRequest,
  CpsDocumentWithAllowedActions,
  DOC_ID_FIELD,
  DEFAULT_ROWS_PER_PAGE,
  DocumentIdAndCt
} from '@cs-cms/types';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import { useSearchContext } from '../../contexts/SearchContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { searchKeys } from '../../queryKeys/search';
import { useNotificationContext } from '../useNotificationContext';

interface DeleteDocumentsMutationContext {
  previousData: CpsSearchQueryResult | undefined;
  previousQueryRequest: CpsSearchQueryRequest | undefined;
}

function getRemainingDocuments(
  searchResult: CpsSearchQueryResult,
  documentsToDelete: DocumentIdAndCt[]
): CpsDocumentWithAllowedActions[] {
  return searchResult.documentsWithPerms.filter(
    item => !documentsToDelete.some(doc => doc.documentId === item.document[DOC_ID_FIELD])
  );
}

function advanceSearchPageAfterPageEmptied(
  queryRequest: CpsSearchQueryRequest,
  totalCount: number,
  deletedCount: number,
  setQueryRequest: Dispatch<SetStateAction<CpsSearchQueryRequest | undefined>>
): boolean {
  const pageSize = queryRequest.size ?? DEFAULT_ROWS_PER_PAGE;
  const skip = queryRequest.skip ?? 0;
  const nextSkip = skip + pageSize;

  if (nextSkip < totalCount) {
    setQueryRequest({ ...queryRequest, skip: nextSkip });
    return true;
  }

  if (skip > 0 && totalCount > 0) {
    const estimatedTotalCount = Math.max(0, totalCount - deletedCount);
    const previousPageSkip = Math.floor((estimatedTotalCount - 1) / pageSize) * pageSize;
    setQueryRequest({ ...queryRequest, skip: Math.max(0, previousPageSkip) });
    return true;
  }

  return false;
}

export function useDeleteDocuments(): UseMutationResult<
  DeleteDocumentsResponse,
  unknown,
  { dto: DeleteDocumentsRequest },
  DeleteDocumentsMutationContext
> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest, setQueryRequest } = useSearchContext();
  const { addCenterNotification } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const deleteDocuments = (data: { dto: DeleteDocumentsRequest }): Promise<DeleteDocumentsResponse> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.deleteDocuments(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.deleteDocuments,
    mutationFn: (data: { dto: DeleteDocumentsRequest }) => deleteDocuments(data),
    onMutate: async data => {
      if (!queryRequest) {
        return { previousData: undefined, previousQueryRequest: undefined };
      }

      await queryClient.cancelQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });

      const previousData = queryClient.getQueryData<CpsSearchQueryResult>(searchKeys.filteredSearch(queryRequest));
      const previousQueryRequest = queryRequest;

      if (!previousData?.documentsWithPerms) {
        return { previousData, previousQueryRequest };
      }

      const remainingDocuments = getRemainingDocuments(previousData, data.dto.documents);

      if (remainingDocuments.length > 0) {
        queryClient.setQueryData<CpsSearchQueryResult>(searchKeys.filteredSearch(queryRequest), {
          ...previousData,
          documentsWithPerms: remainingDocuments
        });
        return { previousData, previousQueryRequest };
      }

      const didAdvancePage = advanceSearchPageAfterPageEmptied(
        queryRequest,
        previousData.totalCount ?? 0,
        data.dto.documents.length,
        setQueryRequest
      );

      if (!didAdvancePage) {
        queryClient.setQueryData<CpsSearchQueryResult>(searchKeys.filteredSearch(queryRequest), {
          ...previousData,
          documentsWithPerms: []
        });
      }

      return { previousData, previousQueryRequest };
    },
    onSuccess: data => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentSuccessMessage', {
          deleted: data.deleted.length,
          total: data.deleted.length + data.skipped.length
        }),
        timestamp: new Date(),
        type: 'success'
      });
    },
    onError: (_err, _newData, context) => {
      if (context?.previousQueryRequest) {
        setQueryRequest(context.previousQueryRequest);
      }

      if (context?.previousQueryRequest && context.previousData) {
        queryClient.setQueryData(searchKeys.filteredSearch(context.previousQueryRequest), context.previousData);
      }

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
